import {Injectable} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from '@angular/router';
import {Observable, throwError} from 'rxjs';
import {catchError, map, take} from 'rxjs/operators';
import {AuthService} from './auth.service';
import {ApiService} from './api.service';
import {CommonUtils} from "../utils/common";

/**
 * Strażnik routingu (Guard) zarządzający najwyższym poziomem uprawnień w systemie Vetineo.
 * Weryfikuje po stronie serwera API, czy zalogowana sesja posiada globalne uprawnienia
 * administracyjne pozwalające na zarządzanie architekturą całej platformy SaaS.
 */
@Injectable({
  providedIn: 'root'
})
export class VetineoAccessGuard implements CanLoad, CanActivate {
  constructor(
    private authService: AuthService,
    private router: Router,
    private apiService: ApiService,
    public commonUtils: CommonUtils
  ) {
  }

  /**
   * Blokuje lub zezwala na asynchroniczne pobranie (Lazy Loading) kodu JavaScript
   * globalnych modułów administracyjnych Vetineo.
   * Chroni wysoce wrażliwą logikę zarządzania systemem przed transferem do przeglądarki
   * zwykłych użytkowników, wymuszając weryfikację na dedykowanym endpointcie API.
   *
   * @param route Konfiguracja chronionej ścieżki.
   * @param segments Segmenty adresu URL.
   * @returns Observable emitujący wartość logiczną (true w przypadku posiadania globalnej roli administracyjnej).
   */
  canLoad(
    route: Route,
    segments: UrlSegment[]
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.apiService.post('/dashboard/vetineo-admin-access', {}).pipe(take(1), catchError(err => {
      if (err.status === 401) {
        this.router.navigate(['signin']).then(() => {
          this.authService.logout();
        });
      }
      return throwError(err);
    }), map(response => {
      console.log('check - vetineo admin - ok');
      return true;
    }));
  }

  /**
   * Weryfikuje uprawnienia tuż przed aktywacją i wyrenderowaniem modułów administracyjnych Vetineo.
   * Przechwytuje błędy wygaśnięcia sesji (HTTP 401) wymuszając
   * bezpieczne wylogowanie, a asynchroniczną obsługę braku uprawnień super-administratora
   * deleguje do ustandaryzowanej metody z klasy CommonUtils.
   *
   * @param route Migawka aktywowanej ścieżki.
   * @param state Migawka stanu routera.
   * @returns Observable emitujący wartość logiczną lub bezpieczne drzewo routingu (UrlTree).
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.apiService.post('/dashboard/vetineo-admin-access', {}).pipe(take(1), catchError(err => {
      if (err.status === 401) {
        this.router.navigate(['signin']).then(() => {
          this.authService.logout();
        });
      }
      return throwError(err);
    }), map(response => {
      console.log('check - canActivate - vetineo admin - ok', response);

      return this.commonUtils.handleHttpResponseShowInfoToast(response, '', 'Nie masz uprawnień do tej strony')
    }));
  }

}
